import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { 
  ArrowLeft, TrendingUp, TrendingDown, Star, AlertCircle, Users,
  Target, Zap, Shield, Award, BarChart3, ThumbsUp, ThumbsDown, Eye, Download, ExternalLink
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../lib/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { Helmet } from 'react-helmet-async';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { FaApple, FaAndroid } from 'react-icons/fa';
import { trackEvent } from '../lib/analytics';

const Analysis = () => {
  const { appId } = useParams();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [appMeta, setAppMeta] = useState({ name: null, storeUrl: null });
  const [isAuthed, setIsAuthed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchAnalysis();
  }, [appId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthed(!!user);
    });
    return () => unsubscribe();
  }, []);

  const fetchAnalysis = async () => {
    try {
      const getAnalysisSummary = httpsCallable(functions, 'getAnalysisSummary');
      const response = await getAnalysisSummary({ appId });
      if (response.data.success) {
        setAnalysis(response.data.data);
        // fetch app meta
        try {
          const storeType = response.data.data.storeType;
          const fnName = storeType === 'apple' ? 'getAppleAppInfo' : 'getGoogleAppInfo';
          const getAppInfo = httpsCallable(functions, fnName);
          const infoRes = await getAppInfo({ appId, country: 'us' });
          const info = infoRes?.data?.data || {};
          const name = info.title || info.trackName || info.name || null;
          const storeUrl = buildStoreUrl(storeType, appId, 'us');
          setAppMeta({ name, storeUrl });
        } catch (_) {
          setAppMeta({ name: null, storeUrl: buildStoreUrl(response.data.data.storeType, appId, 'us') });
        }
      } else {
        setError('Analysis not found');
      }
    } catch (error) {
      console.error('Error fetching analysis:', error);
      setError('Failed to load analysis');
    } finally {
      setLoading(false);
    }
  };

  const buildStoreUrl = (storeType, id, country='us') => {
    if (storeType === 'apple') return `https://apps.apple.com/${country}/app/id${id}`;
    return `https://play.google.com/store/apps/details?id=${id}&hl=en&gl=US`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center">
        <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Error</h2>
        <p className="text-gray-600 mb-4">{error}</p>
        <Link to="/" className="btn-primary"><ArrowLeft className="w-5 h-5 mr-2" />Back to Home</Link>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">No Analysis Found</h2>
        <p className="text-gray-600 mb-4">Please run an analysis first.</p>
        <Link to="/" className="btn-primary"><ArrowLeft className="w-5 h-5 mr-2" />Back to Home</Link>
      </div>
    );
  }

  const { summary, positiveAnalysis, negativeAnalysis } = analysis;
  const visiblePositiveExperiences = (isAuthed ? positiveAnalysis?.positiveExperiences : positiveAnalysis?.positiveExperiences?.slice(0, 1)) || [];
  const visibleTopIssues = (isAuthed ? negativeAnalysis?.topIssues : negativeAnalysis?.topIssues?.slice(0, 1)) || [];

  const rasterizeSvgsForExport = async (container) => {
    const svgNodes = Array.from(container.querySelectorAll('svg'));
    const replacements = [];
    await Promise.all(svgNodes.map(async (svg) => {
      try {
        const rect = svg.getBoundingClientRect();
        const width = Math.ceil(rect.width || svg.viewBox?.baseVal?.width || 0);
        const height = Math.ceil(rect.height || svg.viewBox?.baseVal?.height || 0);
        if (!width || !height) return;
        const svgClone = svg.cloneNode(true);
        svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        svgClone.setAttribute('width', String(width));
        svgClone.setAttribute('height', String(height));
        const svgString = new XMLSerializer().serializeToString(svgClone);
        const svgDataUri = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
        const image = new Image();
        image.src = svgDataUri;
        await new Promise((resolve) => { image.onload = resolve; image.onerror = resolve; });
        const scale = Math.max(1, Math.floor(window.devicePixelRatio || 1));
        const offscreen = document.createElement('canvas');
        offscreen.width = width * scale;
        offscreen.height = height * scale;
        const ctx = offscreen.getContext('2d');
        ctx.scale(scale, scale);
        ctx.drawImage(image, 0, 0, width, height);
        const imgEl = new Image();
        imgEl.src = offscreen.toDataURL('image/png');
        imgEl.style.width = `${width}px`;
        imgEl.style.height = `${height}px`;
        const parent = svg.parentNode;
        if (!parent) return;
        parent.replaceChild(imgEl, svg);
        replacements.push({ parent, imgEl, svg });
      } catch (_) {}
    }));
    return () => { for (const { parent, imgEl, svg } of replacements) { try { parent.replaceChild(svg, imgEl); } catch (_) {} } };
  };

  const handleExportPdf = async () => {
    trackEvent('export_report_pdf', { app_id: appId });
    const reportEl = document.getElementById('analysis-report');
    if (!reportEl) return;
    let revertSvgs = () => {};
    try {
      revertSvgs = await rasterizeSvgsForExport(reportEl);
      const canvas = await html2canvas(reportEl, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false });
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
      const pageWidthMm = pdf.internal.pageSize.getWidth();
      const pageHeightMm = pdf.internal.pageSize.getHeight();
      const marginMm = 10;
      const contentWidthMm = pageWidthMm - marginMm * 2;
      const contentHeightMm = pageHeightMm - marginMm * 2;
      const pxPerMm = canvas.width / contentWidthMm;
      const pageHeightPx = Math.floor(contentHeightMm * pxPerMm);
      let currentY = 0, pageIndex = 0;
      while (currentY < canvas.height) {
        const sliceHeightPx = Math.min(pageHeightPx, canvas.height - currentY);
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeightPx;
        const ctx = pageCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, currentY, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);
        const imgData = pageCanvas.toDataURL('image/jpeg', 1);
        const sliceHeightMm = sliceHeightPx / pxPerMm;
        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', marginMm, marginMm, contentWidthMm, sliceHeightMm);
        currentY += sliceHeightPx;
        pageIndex += 1;
      }
      pdf.save(`analysis_${appId}.pdf`);
    } catch (e) { console.error('Failed to export PDF:', e); }
    finally { try { revertSvgs(); } catch (_) {} }
  };

  const totalPosNeg = (summary?.positiveCount || 0) + (summary?.negativeCount || 0);
  const formatNumber = (n) => (typeof n === 'number' ? n.toLocaleString() : '0');

  const sentimentData = [
    { name: 'Positive', value: summary.positiveCount, color: '#28a745' },
    { name: 'Negative', value: summary.negativeCount, color: '#dc3545' },
    { name: 'Neutral', value: summary.neutralCount, color: '#ffc107' }
  ];

  const ratingData = [
    { rating: '5★', count: summary.positiveCount },
    { rating: '4★', count: summary.positiveCount },
    { rating: '3★', count: summary.neutralCount },
    { rating: '2★', count: summary.negativeCount },
    { rating: '1★', count: summary.negativeCount }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Helmet>
        <title>{`${appMeta.name || appId} Review Analysis | AppReview.ai`}</title>
        <meta name="description" content={`AI-powered review analysis for ${appMeta.name || appId}: sentiment breakdown, top issues, strengths, and actionable recommendations.`} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={`https://appreview.ai/analysis/${appId}`} />
        <meta property="og:url" content={`https://appreview.ai/analysis/${appId}`} />
        <meta property="og:title" content={`${appMeta.name || appId} Review Analysis | AppReview.ai`} />
        <meta property="og:description" content={`AI insights for ${appMeta.name || appId}: sentiment, top issues, strengths, and recommendations.`} />
        <meta property="og:type" content="article" />
      </Helmet>
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex items-start sm:items-center space-x-3 sm:space-x-4">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-2 sm:p-3 rounded-xl">
                <BarChart3 className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  {analysis.storeType === 'apple' ? <FaApple className="w-5 h-5 sm:w-6 sm:h-6 text-black" /> : <FaAndroid className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />}
                  <h1 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900 truncate">{appMeta.name || 'App'}</h1>
                  {appMeta.storeUrl && (
                    <a href={appMeta.storeUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-xs sm:text-sm text-blue-600 hover:text-blue-800 underline">
                      View on {analysis.storeType === 'apple' ? 'App Store' : 'Google Play'}<ExternalLink className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                    </a>
                  )}
                </div>
                <p className="text-gray-600 mt-1 text-xs sm:text-sm">ID: {appId} <span className="mx-2">•</span> Analyzed {new Date(analysis.timestamp).toLocaleDateString()}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <button className="btn-secondary flex items-center text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" onClick={isAuthed ? handleExportPdf : () => navigate('/login')}>
                <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />{isAuthed ? 'Export Report' : 'Login to Export'}
              </button>
              <Link to="/" className="btn-primary flex items-center text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2">
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />New Analysis
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div id="analysis-report" className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-white rounded-xl">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between"><div><p className="text-xs sm:text-sm font-medium text-gray-600">Total Reviews</p><p className="text-2xl sm:text-3xl font-bold text-gray-900">{formatNumber(totalPosNeg)}</p></div><div className="bg-blue-100 p-2 sm:p-3 rounded-lg"><Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" /></div></div>
          </div>
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between"><div><p className="text-xs sm:text-sm font-medium text-gray-600">Average Rating</p><p className="text-2xl sm:text-3xl font-bold text-gray-900">{summary.averageRating.toFixed(1)}</p><div className="flex items-center mt-1">{[...Array(5)].map((_, i) => (<Star key={i} className={`w-3 h-3 sm:w-4 sm:h-4 ${i < Math.floor(summary.averageRating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />))}</div></div><div className="bg-yellow-100 p-2 sm:p-3 rounded-lg"><Star className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" /></div></div>
          </div>
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between"><div><p className="text-xs sm:text-sm font-medium text-gray-600">Positive Sentiment</p><p className="text-2xl sm:text-3xl font-bold text-green-600">{summary.positivePercentage.toFixed(1)}%</p><p className="text-xs sm:text-sm text-gray-500">{summary.positiveCount} reviews</p></div><div className="bg-green-100 p-2 sm:p-3 rounded-lg"><ThumbsUp className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" /></div></div>
          </div>
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between"><div><p className="text-xs sm:text-sm font-medium text-gray-600">Negative Sentiment</p><p className="text-2xl sm:text-3xl font-bold text-red-600">{summary.negativePercentage.toFixed(1)}%</p><p className="text-xs sm:text-sm text-gray-500">{summary.negativeCount} reviews</p></div><div className="bg-red-100 p-2 sm:p-3 rounded-lg"><ThumbsDown className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" /></div></div>
          </div>
        </div>

        {/* Charts */}
        <div className={(isAuthed ? '' : 'filter blur-sm md:blur pointer-events-none ') + 'grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8'}>
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8">
            <div className="flex items-center mb-4 sm:mb-6"><Eye className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 mr-2 sm:mr-3" /><h3 className="text-lg sm:text-xl font-bold text-gray-900">Sentiment Distribution</h3></div>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart><Pie data={sentimentData} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} outerRadius={80} fill="#8884d8" dataKey="value">{sentimentData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.color} />))}</Pie><Tooltip /></PieChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8">
            <div className="flex items-center mb-4 sm:mb-6"><BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 mr-2 sm:mr-3" /><h3 className="text-lg sm:text-xl font-bold text-gray-900">Rating Distribution</h3></div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={ratingData}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="rating" /><YAxis /><Tooltip /><Bar dataKey="count" fill="url(#colorGradient)" radius={[4, 4, 0, 0]} /><defs><linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#667eea" stopOpacity={0.8}/><stop offset="95%" stopColor="#764ba2" stopOpacity={0.8}/></linearGradient></defs></BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Positive & Negative Analysis */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          {positiveAnalysis && (
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
                <div className="flex items-center"><TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-white mr-3 sm:mr-4" /><div><h2 className="text-lg sm:text-2xl font-bold text-white">Strengths & Positives</h2><p className="text-green-100 text-sm sm:text-base">What users love about your app</p></div></div>
              </div>
              <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
                <div><div className="flex items-center mb-3 sm:mb-4"><Award className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mr-2" /><h3 className="text-base sm:text-lg font-bold text-gray-900">Top-Rated Features</h3></div>
                  <div className={isAuthed ? 'space-y-2 sm:space-y-3' : 'space-y-2 sm:space-y-3 filter blur-[1px]'}>
                    {positiveAnalysis.topFeatures?.slice(0, isAuthed ? undefined : 1).map((feature, index) => (
                      <div key={index} className="flex items-center p-2 sm:p-3 bg-green-50 rounded-lg sm:rounded-xl"><div className="bg-green-500 text-white rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-xs sm:text-sm font-bold mr-2 sm:mr-3">{index + 1}</div><span className="text-gray-800 font-medium text-sm sm:text-base">{feature}</span></div>
                    ))}
                  </div>
                </div>
                <div><div className="flex items-center mb-3 sm:mb-4"><Users className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mr-2" /><h3 className="text-base sm:text-lg font-bold text-gray-900">Positive Experiences</h3></div>
                  <div className="space-y-2 sm:space-y-3">{visiblePositiveExperiences.map((exp, i) => (
                    <div key={i} className="flex items-start p-2 sm:p-3 bg-green-50 rounded-lg sm:rounded-xl"><Star className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 mr-2 sm:mr-3 mt-0.5 flex-shrink-0" /><span className="text-gray-800 text-sm sm:text-base">{exp}</span></div>
                  ))}</div>
                </div>
                <div><div className="flex items-center mb-3 sm:mb-4"><Shield className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mr-2" /><h3 className="text-base sm:text-lg font-bold text-gray-900">Strategic Recommendations</h3></div>
                  <div className={isAuthed ? 'space-y-2 sm:space-y-3' : 'space-y-2 sm:space-y-3 filter blur-[1px]'}>
                    {positiveAnalysis.strengthHighlights?.slice(0, isAuthed ? undefined : 1).map((h, i) => (
                      <div key={i} className="p-3 sm:p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg sm:rounded-xl border-l-4 border-green-500"><p className="text-gray-800 leading-relaxed text-sm sm:text-base">{h}</p></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          {negativeAnalysis && (
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-red-500 to-pink-600 px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
                <div className="flex items-center"><TrendingDown className="w-6 h-6 sm:w-8 sm:h-8 text-white mr-3 sm:mr-4" /><div><h2 className="text-lg sm:text-2xl font-bold text-white">Issues & Improvements</h2><p className="text-red-100 text-sm sm:text-base">Areas that need attention</p></div></div>
              </div>
              <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
                <div><div className="flex items-center mb-3 sm:mb-4"><AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mr-2" /><h3 className="text-base sm:text-lg font-bold text-gray-900">Most Common Issues</h3></div>
                  <div className="space-y-2 sm:space-y-3">{visibleTopIssues.map((issue, i) => (
                    <div key={i} className="flex items-center p-2 sm:p-3 bg-red-50 rounded-lg sm:rounded-xl"><div className="bg-red-500 text-white rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-xs sm:text-sm font-bold mr-2 sm:mr-3">{i + 1}</div><span className="text-gray-800 font-medium text-sm sm:text-base">{issue}</span></div>
                  ))}</div>
                </div>
                <div><div className="flex items-center mb-3 sm:mb-4"><Target className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mr-2" /><h3 className="text-base sm:text-lg font-bold text-gray-900">Critical Problems</h3></div>
                  <div className={isAuthed ? 'space-y-2 sm:space-y-3' : 'space-y-2 sm:space-y-3 filter blur-[1px]'}>
                    {negativeAnalysis.criticalProblems?.slice(0, isAuthed ? undefined : 1).map((p, i) => (
                      <div key={i} className="flex items-start p-2 sm:p-3 bg-red-50 rounded-lg sm:rounded-xl"><AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 mr-2 sm:mr-3 mt-0.5 flex-shrink-0" /><span className="text-gray-800 text-sm sm:text-base">{p}</span></div>
                    ))}
                  </div>
                </div>
                <div><div className="flex items-center mb-3 sm:mb-4"><Zap className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mr-2" /><h3 className="text-base sm:text-lg font-bold text-gray-900">Action Plan</h3></div>
                  <div className={isAuthed ? 'space-y-3 sm:space-y-4' : 'space-y-3 sm:space-y-4 filter blur-[1px]'}>
                    {negativeAnalysis.suggestedImprovements?.slice(0, isAuthed ? undefined : 1).map((imp, i) => (
                      <div key={i} className="p-3 sm:p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-lg sm:rounded-xl border-l-4 border-red-500">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-3">
                          <h4 className="font-bold text-gray-900 text-sm sm:text-base mb-1 sm:mb-0">{imp.issue}</h4>
                          <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-bold inline-block ${imp.priority === 'High' ? 'bg-red-100 text-red-800' : imp.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>{imp.priority} Priority</span>
                        </div>
                        <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{imp.improvement}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {!isAuthed && (
        <div className="fixed inset-0 pointer-events-none flex items-center justify-center p-4">
          <div className="pointer-events-auto backdrop-blur-sm bg-white/70 border border-red-200 rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 max-w-sm sm:max-w-md text-center">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Unlock Full Report</h3>
            <p className="text-sm sm:text-base text-gray-700 mb-4">Sign in to view detailed charts and recommendations.</p>
            <button onClick={() => navigate('/login', { state: { redirectTo: location.pathname + location.search } })} className="inline-flex items-center px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm sm:text-base">Unblock Content</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analysis;
